import { sdkApi } from '../api';
import { tasksSlice } from './tasksSlice';
import TCClient from 'client';
import type TasksService from 'js-sdk/src/services/TasksService';
import type { CreateTaskPayload, Task } from 'js-sdk/src/types/Tasks';
import type { SDKQueryErrorType } from 'types/api';

const { setLinkedTask, setTasks } = tasksSlice.actions;

function getTasksClient(): TasksService {
  return (TCClient as unknown as { tasks: TasksService }).tasks;
}

function toSdkQueryError(error: unknown): SDKQueryErrorType {
  return { reason: error instanceof Error ? error.message : String(error), code: 0 };
}

const tasksApi = sdkApi.injectEndpoints({
  endpoints: (build) => ({
    createTask: build.mutation<Task, { organizationId: string; payload: CreateTaskPayload }>({
      queryFn: async ({ organizationId, payload }) => {
        try {
          const data = await getTasksClient().create(organizationId, payload);
          return { data };
        } catch (error) {
          return { error: toSdkQueryError(error) };
        }
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data: task } = await queryFulfilled;
          dispatch(setLinkedTask(task));
        } catch {
          // Failure is surfaced to the modal via the mutation's error state.
        }
      },
    }),
    completeTask: build.mutation<Task, { organizationId: string; taskId: string }>({
      queryFn: async ({ organizationId, taskId }) => {
        try {
          const data = await getTasksClient().complete(organizationId, taskId);
          return { data };
        } catch (error) {
          return { error: toSdkQueryError(error) };
        }
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data: task } = await queryFulfilled;
          dispatch(setLinkedTask(task));
        } catch {
          // no-op — surfaced via mutation error state.
        }
      },
    }),
    reopenTask: build.mutation<Task, { organizationId: string; taskId: string }>({
      queryFn: async ({ organizationId, taskId }) => {
        try {
          const data = await getTasksClient().reopen(organizationId, taskId);
          return { data };
        } catch (error) {
          return { error: toSdkQueryError(error) };
        }
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data: task } = await queryFulfilled;
          dispatch(setLinkedTask(task));
        } catch {
          // no-op — surfaced via mutation error state.
        }
      },
    }),
    getTasks: build.query<Task[], string>({
      queryFn: async (organizationId) => {
        try {
          const data = await getTasksClient().list(organizationId);
          return { data };
        } catch (error) {
          return { error: toSdkQueryError(error) };
        }
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data: tasks } = await queryFulfilled;
          dispatch(setTasks(tasks));
        } catch {
          // no-op — surfaced via query error state.
        }
      },
    }),
  }),
  overrideExisting: true,
});

export const {
  useCreateTaskMutation,
  useCompleteTaskMutation,
  useReopenTaskMutation,
  useGetTasksQuery,
} = tasksApi;

export { tasksApi };
